<template>
    <div v-if="isOpen" class="fixed inset-0 z-[100001] flex items-center justify-center bg-black/70"
        @click="handleClickOutside">
        <div class="relative w-full max-w-2xl p-0 mx-auto overflow-hidden bg-white shadow-2xl rounded-xl" @click.stop>
            <!-- Header with title and close button -->
            <div class="flex items-center justify-between p-4 border-b bg-gray-50">
                <h3 class="text-lg font-semibold text-gray-800">{{ title }}</h3>
                <button type="button" aria-label="Close" @click="closeModal"
                    class="text-gray-500 transition hover:text-gray-700">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
            <div class="p-6">
                <slot>
                    <div class="flex flex-col gap-6 mb-6 md:flex-row">
                        <!-- Full responsive image -->
                        <div class="flex justify-center w-full md:w-1/3">
                            <img :src="getFotoUrl(formasi.dosen.foto, formasi.dosen.nama)" alt="Foto Dosen"
                                class="w-full max-w-[200px] h-auto object-cover border rounded-lg shadow-lg" />
                        </div>
                        <!-- Dosen information -->
                        <div class="flex flex-col justify-center w-full md:w-2/3">
                            <table class="min-w-full border-separate border-spacing-y-3">
                                <tbody>
                                    <tr>
                                        <td class="w-1/4 align-top">
                                            <span class="font-medium text-gray-900">Nama</span>
                                        </td>
                                        <td class="w-3/4 px-3">
                                            <span class="text-gray-700">{{
                                                formasi.dosen.nama || "-"
                                            }}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-1/4 align-top">
                                            <span class="font-medium text-gray-900">NIP</span>
                                        </td>
                                        <td class="w-3/4 px-3">
                                            <span class="text-gray-700">{{
                                                formasi.dosen.nip || "-"
                                            }}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-1/4 align-top">
                                            <span class="font-medium text-gray-900">NIDN</span>
                                        </td>
                                        <td class="w-3/4 px-3">
                                            <span class="text-gray-700">{{
                                                formasi.dosen.nidn || "-"
                                            }}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-1/4 align-top">
                                            <span class="font-medium text-gray-900">Email</span>
                                        </td>
                                        <td class="w-3/4 px-3">
                                            <span class="text-gray-700">{{
                                                formasi.dosen.email || "-"
                                            }}</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="w-1/4 align-top">
                                            <span class="font-medium text-gray-900">CV</span>
                                        </td>
                                        <td class="w-3/4 px-3">
                                            <span class="text-gray-700 uppercase">
                                                <a href="#">Profil Lengkap</a>
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <table class="min-w-full mt-6 border-separate border-spacing-y-2">
                        <tbody>
                            <tr>
                                <td class="w-1/4 align-top">
                                    <span class="font-medium text-gray-900">Pendidikan</span>
                                </td>
                                <td class="w-3/4 px-3">
                                    <ul class="pl-5 m-0 text-gray-700 list-disc">
                                        <li v-for="(item, idx) in pendidikanList" :key="idx" class="mb-1">
                                            {{ item }}
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-1/4 align-top">
                                    <span class="font-medium text-gray-900">Jabatan</span>
                                </td>
                                <td class="w-3/4 px-3">
                                    <ol class="pl-5 m-0 text-gray-700 list-decimal">
                                        <li class="mb-1">{{ formasi.jabatan.namajabatan }}</li>
                                        <li class="mb-1">-</li>
                                    </ol>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-1/4 align-top">
                                    <span class="font-medium text-gray-900">Jabatan Akademik</span>
                                </td>
                                <td class="w-3/4 px-3">
                                    <span v-if="jabAkademikText" class="text-gray-700 uppercase">
                                        {{ jabAkademikText }}
                                    </span>
                                    <span v-else class="text-gray-700">-</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Close button at bottom -->
                    <div class="flex justify-end mt-6">
                        <button type="button" @click="closeModal"
                            class="px-4 py-2 text-gray-800 transition-colors bg-gray-200 rounded-md hover:bg-gray-300">
                            Tutup
                        </button>
                    </div>
                </slot>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits, watch, ref, reactive, computed } from "vue";

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    title: {
        type: String,
        required: true,
    },
    formasi: {
        type: Array,
        required: true,
    },
});

const emit = defineEmits(["update:isOpen"]);

// Close modal function
const closeModal = () => {
    emit("update:isOpen", false);
};

// Handle clicking outside the modal to close it
const handleClickOutside = (event) => {
    if (event.target === event.currentTarget) {
        closeModal();
    }
};
function getFotoUrl(foto, nama = "User") {
    if (!foto) {
        const initials = nama
            .split(" ")
            .map((n) => n[0])
            .join("")
            .substring(0, 2);
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(
            initials
        )}&background=e5e7eb&color=6b7280&size=128`;
    }
    if (/^https?:\/\//.test(foto) || foto.startsWith("/storage")) {
        return foto;
    }
    return "/storage/" + foto;
}
const pendidikanList = computed(() => {
    // Ambil dari props.formasi, bukan variabel global
    const dosen = props.formasi && props.formasi.dosen ? props.formasi.dosen : {};
    const raw = dosen.pendidikan || "";
    let text = raw
        .replace(/<li>/g, "\n")
        .replace(/<[^>]+>/g, "")
        .split(/\n|<br\s*\/?>(?=<)/i)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    return text;
});

const jabAkademikText = computed(() => {
    const dosen = props.formasi && props.formasi.dosen ? props.formasi.dosen : {};
    // Ambil plain text dari jabakademik, hilangkan tag HTML jika ada
    const raw = dosen.jabakademik || "";
    return raw.replace(/<[^>]+>/g, "").trim();
});
</script>

<style scoped>
/* Add responsive styling */
@media (max-width: 640px) {
    .max-w-2xl {
        width: 85%;
        /* Reduced from 95% to make modal medium sized on mobile */
        max-width: 450px;
        /* Set a max-width for medium size feel */
        margin: 0 auto;
    }

    /* Adjust padding for better spacing on mobile */
    .p-6 {
        padding: 1rem;
    }

    /* Keep image proportional but not too big */
    .max-w-\[200px\] {
        max-width: 150px;
    }
}

/* Add smooth transitions */
.fixed {
    transition: all 0.3s ease-in-out;
}

/* Center modal content on all screen sizes */
.flex.justify-center {
    align-items: center;
}
</style>
